import React, { FormEvent, useState, ChangeEvent, useContext, MouseEvent, useEffect } from 'react';
import { CouponContext } from '../../contexts/CouponContext';
import Logo from '../../components/Logo';
import {
  FormWrapper,
  CouponInput,
  Header,
  InputLabel,
  InputWrapper,
  CouponSelect,
  CouponFormControl,
  FormDatePicker,
  DatePickerWrapper,
  ContentWrapper,
  Message,
} from './styled';
import { Container, LogoWrapper, Step } from '../../components/FormElements';
import { FormButton } from '../../components/FormButton';
import { Option } from '@spaceshipapp/cambridge';
import 'react-datepicker/dist/react-datepicker.css';
import { dateFormatter } from '../../utils/dateFormatter';
import { useNavigate } from 'react-router-dom';
import CountrySelect from '../../components/CountrySelect';
import { getExpiredTime, getToken, removeToken } from '../../utils/auth';

const CouponDetails = (): React.ReactElement => {
  const [type, setType] = useState('');
  const [value, setValue] = useState('');
  const [minAmount, setMinAmount] = useState('');
  const [route, setRoute] = useState('');
  const [from, setFrom] = useState<any>();
  const [destination, setDestination] = useState<any>();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [expiredDate, setExpiredDate] = useState(new Date());
  const { couponCode, setCouponDetails } = useContext(CouponContext);
  const navigate = useNavigate();

  const selectorValidator = (type: string, route: string, from: any, destination: any) => {
    if (!type || !route || !from || !destination) {
      switch (true) {
        case !type:
          setError('Please select a type!');
          break;
        case !route:
          setError('Please select a route!');
          break;
        case route === 'route' && (!from || !destination):
          setError('Please select origin and destination!');
          break;
        default:
          return;
      }
    } else {
      return;
    }
  };
  const confirmDetails = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(!loading);
    setError('');
    selectorValidator(type, route, from, destination);

    const couponDetails = couponCode?.map((code) => {
      return {
        code,
        type: type ?? 'fixed',
        value: Number(value),
        applies: 'shipment',
        rules: {
          amount: minAmount,
          route: route ?? 'all',
          from: `${from?.value}` ?? '',
          to: `${destination?.value}` ?? '',
        },
        services: '[]',
        users: '',
        startAt: dateFormatter(startDate),
        expiresAt: dateFormatter(expiredDate),
        isDisposable: 1,
        isActive: 1,
        viaResource: '',
        viaResourceId: '',
        viaRelationship: '',
      };
    });
    setCouponDetails(couponDetails);

    if (couponDetails && !error) {
      navigate('/confirm');
      setLoading(false);
      setType('');
      setValue('');
      setMinAmount('');
      setRoute('');
      setFrom('');
      setDestination('');
      setStartDate(new Date());
      setExpiredDate(new Date());
    }
  };

  useEffect(() => {
    function fetchToken() {
      const token = getToken();
      const tokenExpired = Date.now() - Number(getExpiredTime()) > 0 ? true : false;
      if (!token || tokenExpired) {
        removeToken();
        navigate('/');
      }
      if (!token) {
        navigate('/');
      }
    }
    fetchToken();
  }, []);

  return (
    <>
      <Container>
        <FormWrapper onSubmit={(event: FormEvent<HTMLFormElement>) => confirmDetails(event)}>
          <LogoWrapper>
            <Logo />
            <Step>2/3</Step>
          </LogoWrapper>
          <Header>Coupon Details</Header>
          <Message>{error && error}</Message>
          <ContentWrapper>
            <InputWrapper>
              <InputLabel>Type</InputLabel>
              <CouponFormControl>
                <CouponSelect
                  id="type"
                  selectedItem={type}
                  onChange={(value) => {
                    setType(value);
                  }}
                  isError={loading && !type && true}
                >
                  <Option value="fixed">Fixed</Option>
                  <Option value="percentage">Percentage</Option>
                </CouponSelect>
              </CouponFormControl>
            </InputWrapper>
            <InputWrapper>
              <InputLabel>Value (HKD)</InputLabel>
              <CouponInput
                required
                placeholder="Discount Value"
                value={value}
                type="number"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  return setValue(e.target.value);
                }}
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>Min. (HKD)</InputLabel>
              <CouponInput
                placeholder="Min. Amount"
                value={minAmount}
                type="number"
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  return setMinAmount(e.target.value);
                }}
              />
            </InputWrapper>
            <InputWrapper>
              <InputLabel>Route</InputLabel>
              <CouponFormControl>
                <CouponSelect
                  id="route"
                  selectedItem={route}
                  onChange={(value) => {
                    setRoute(value);
                  }}
                  isError={loading && !route && true}
                >
                  <Option value="all">All Route</Option>
                  <Option value="import">Import</Option>
                  <Option value="export">Export</Option>
                  <Option value="route">Specific Route</Option>
                </CouponSelect>
              </CouponFormControl>
            </InputWrapper>
            {route === 'route' ? (
              <>
                <InputWrapper>
                  <InputLabel>From</InputLabel>
                  <CouponFormControl>
                    <CountrySelect
                      value={from}
                      onChange={(value) => {
                        setFrom(value);
                      }}
                    />
                  </CouponFormControl>
                </InputWrapper>
                <InputWrapper>
                  <InputLabel>To</InputLabel>
                  <CouponFormControl>
                    <CountrySelect
                      value={destination}
                      onChange={(value) => {
                        setDestination(value);
                      }}
                    />
                  </CouponFormControl>
                </InputWrapper>
              </>
            ) : null}

            <InputWrapper>
              <InputLabel>Start Date</InputLabel>
              <DatePickerWrapper>
                <FormDatePicker
                  showTimeSelect
                  selected={startDate}
                  onChange={(date: Date) => setStartDate(date)}
                  dateFormat="yyyy-MM-dd hh:mm:ss"
                />
              </DatePickerWrapper>
            </InputWrapper>
            <InputWrapper>
              <InputLabel>Expired Date</InputLabel>
              <DatePickerWrapper>
                <FormDatePicker
                  showTimeSelect
                  selected={expiredDate}
                  onChange={(date: Date) => setExpiredDate(date)}
                  dateFormat="yyyy-MM-dd hh:mm:ss"
                />
              </DatePickerWrapper>
            </InputWrapper>
          </ContentWrapper>
          <FormButton variant="primary" size="sm" type="submit">
            Next
          </FormButton>
          <FormButton
            variant="primary"
            size="sm"
            onClick={(e: MouseEvent<HTMLButtonElement>) => {
              e.preventDefault();
              navigate('/form');
            }}
          >
            Back
          </FormButton>
        </FormWrapper>
      </Container>
    </>
  );
};

export default CouponDetails;
